import { BOOKING_FILTERS } from '@/enums/calendar'
import { CLIENT_FILTERS } from '@/enums/client'
import { PARTNER_COMPANY_FILTERS } from '@/enums/partnership'
import { PARTNER_COMPANY_INVITE_FILTERS } from '@/enums/partnershipInvite'
import { RESOURCE_FILTERS } from '@/enums/resources'
import {
  COMPUCASH_IMPORT_FILTERS,
  getDefaultPrivateStateOption,
  getServiceStateOptionList,
  ROUTE_PLANNER_FILTERS,
  SERVICE_EVENT_FILTERS,
  SERVICE_FILTERS,
  SERVICE_STATE_FILTER,
} from '@/enums/services'
import { TASK_FILTERS } from '@/enums/tasks'
import { TICKET_FILTERS } from '@/enums/ticket'
import {
  BookingFilterState,
  FilterState,
  PartnershipFilterState,
  PartnershipInviteFilterState,
  ProductFilterState,
  ResourceFilterState,
  ServiceFilterState,
  TaskFilterState,
  TicketFilterState,
} from '@/models/shared.model'
import { defineStore } from 'pinia'

const serviceEventDefaultFilterValues = () => {
  return {
    date: null,
    showArchived: false,
  }
}

const getPartnershipInviteFilterDefaultValues = () => {
  return {
    filters: {
      search: '',
      status: null,
    },
    ordering: {
      key: 'created_at',
      value: false,
      asc: 'created_at',
      desc: '-created_at',
    },
  }
}

const getPartnershipFilterDefaultValues = () => {
  return {
    filters: {
      search: '',
      status: null,
    },
    ordering: {
      key: 'created_at',
      value: false,
      asc: 'created_at',
      desc: '-created_at',
    },
  }
}

const getTicketFilterDefaultValues = () => {
  return {
    filters: {
      search: '',
      service_hash_ids: [],
      is_cancelled: null,
      activated_at_range: null,
      valid_range: null,
      validated_at_range: null,
    },
    ordering: {
      key: 'activated_at',
      value: false,
      asc: 'activated_at',
      desc: '-activated_at',
    },
  }
}

export const useFilterStore = defineStore('filter', {
  state: (): FilterState => ({
    clientFilter: {
      filters: {
        search: '',
        isBusinessClient: false,
      },
    },
    serviceFilter: {
      filters: {
        search: '',
        categories: [],
        type: null,
        state:
          getServiceStateOptionList().find(
            (state) => state.key === SERVICE_STATE_FILTER.SHOW_ACTIVE,
          ) || getServiceStateOptionList()[1],
        private_state: getDefaultPrivateStateOption(),
        labels: [],
      },
      ordering: {
        key: null,
        value: null,
        asc: null,
        desc: null,
      },
    },
    serviceEventFilter: {
      filters: serviceEventDefaultFilterValues(),
    },
    bookingFilter: {
      filters: {
        search: '',
        type: null,
        date: null,
        reservationStatuses: [],
        payoutStatuses: [],
      },
      ordering: {
        key: null,
        value: null,
        asc: null,
        desc: null,
      },
    },
    resourceFilter: {
      filters: {
        type: null,
        relatedUser: [],
      },
      ordering: {
        key: null,
        value: null,
        asc: null,
        desc: null,
      },
    },
    taskFilter: {
      filters: {
        search: '',
        recipient: [],
        period: null,
        service: [],
        status: null,
      },
      ordering: {
        key: null,
        value: null,
        asc: null,
        desc: null,
      },
    },
    partnershipInviteFilter: getPartnershipInviteFilterDefaultValues(),
    partnershipFilter: getPartnershipFilterDefaultValues(),
    ticketFilter: getTicketFilterDefaultValues(),
    productFilter: {
      filters: {
        search: '',
        without_service: false,
      },
      ordering: {
        key: null,
        value: null,
        asc: null,
        desc: null,
      },
    },
    routeServiceFilter: {
      filters: {
        search: '',
        searchCategories: '',
        dmoMap: '',
      },
    },
  }),

  getters: {
    // ---------- Client ----------
    clientFilterObj: (state) => state.clientFilter,
    // ---------- Service ----------
    serviceFilterObj: (state) => state.serviceFilter,
    // ---------- Service event ----------
    serviceEventFilterObj: (state) => state.serviceEventFilter,
    isServiceEventFilterDefault(state) {
      return (
        JSON.stringify(state.serviceEventFilter.filters) ===
        JSON.stringify(serviceEventDefaultFilterValues())
      )
    },
    // ---------- Booking ----------
    bookingFilterObj: (state) => state.bookingFilter,
    // ---------- Resource ----------
    resourceFilterObj: (state) => state.resourceFilter,
    // ---------- Task ----------
    taskFilterObj: (state) => state.taskFilter,
    // ---------- Partner Company Invite ----------
    partnershipInviteFilterObj: (state) => state.partnershipInviteFilter,
    // ---------- Partner Company ----------
    partnershipFilterObj: (state) => state.partnershipFilter,
    // ---------- Tickets ----------
    ticketFilterObj: (state) => state.ticketFilter,
  },

  actions: {
    updateClientFilters(value: any, filter: CLIENT_FILTERS) {
      if (CLIENT_FILTERS.SEARCH === filter) {
        this.clientFilter.filters.search = value
      } else if (CLIENT_FILTERS.IS_BUSINESS_CLIENT === filter) {
        this.clientFilter.filters.isBusinessClient = value
      }
    },
    updateServiceFilters(value: any, filter: SERVICE_FILTERS) {
      if (SERVICE_FILTERS.CATEGORIES === filter) {
        this.serviceFilter.filters.categories = value
      } else if (SERVICE_FILTERS.TYPE === filter) {
        this.serviceFilter.filters.type = value
      } else if (SERVICE_FILTERS.STATE === filter) {
        this.serviceFilter.filters.state = value
      } else if (SERVICE_FILTERS.PRIVATE_STATE === filter) {
        this.serviceFilter.filters.private_state = value
      } else if (SERVICE_FILTERS.LABELS === filter) {
        this.serviceFilter.filters.labels = value
      } else if (SERVICE_FILTERS.SEARCH === filter) {
        this.serviceFilter.filters.search = value
      } else {
        this.serviceFilter.ordering = value
      }
    },
    updateServiceEventFilters(value: any, filter: SERVICE_EVENT_FILTERS) {
      if (SERVICE_EVENT_FILTERS.DATE === filter) {
        if (value) {
          value.setHours(0, 0, 0, 0)
        }
        this.serviceEventFilter.filters.date = value
      } else if (SERVICE_EVENT_FILTERS.SHOW_ARCHIVED === filter) {
        this.serviceEventFilter.filters.showArchived = value
      }
    },
    updateBookingFilters(value: any, filter: BOOKING_FILTERS) {
      if (BOOKING_FILTERS.SEARCH === filter) {
        this.bookingFilter.filters.search = value
      } else if (BOOKING_FILTERS.TYPE === filter) {
        this.bookingFilter.filters.type = value
      } else if (BOOKING_FILTERS.DATE_RANGE === filter) {
        this.bookingFilter.filters.date = value
      } else if (BOOKING_FILTERS.RESERVATION_STATUSES === filter) {
        this.bookingFilter.filters.reservationStatuses = value
      } else if (BOOKING_FILTERS.PAYOUT_STATUSES === filter) {
        this.bookingFilter.filters.payoutStatuses = value
      } else {
        this.bookingFilter.ordering = value
      }
    },
    updateResourceFilters(value: any, filter: RESOURCE_FILTERS) {
      if (RESOURCE_FILTERS.TYPE === filter) {
        this.resourceFilter.filters.type = value
      } else if (RESOURCE_FILTERS.RELATED_USER === filter) {
        this.resourceFilter.filters.relatedUser = value
      } else {
        this.resourceFilter.ordering = value
      }
    },
    updateTaskFilters(value: any, filter: TASK_FILTERS) {
      if (TASK_FILTERS.SEARCH === filter) {
        this.taskFilter.filters.search = value
      } else if (TASK_FILTERS.RECIPIENT === filter) {
        this.taskFilter.filters.recipient = value
      } else if (TASK_FILTERS.PERIOD === filter) {
        this.taskFilter.filters.period = value
      } else if (TASK_FILTERS.SERVICE === filter) {
        this.taskFilter.filters.service = value
      } else if (TASK_FILTERS.STATUS === filter) {
        this.taskFilter.filters.status = value
      } else {
        this.taskFilter.ordering = value
      }
    },
    updatePartnershipInviteFilters(
      value: any,
      filter?: PARTNER_COMPANY_INVITE_FILTERS,
    ) {
      // TODO: Others over to this order as well.
      // Ordering should be updated on purpose not default to when filter does not match.
      if (filter === undefined) {
        this.partnershipInviteFilterObj.ordering = value
      } else if (PARTNER_COMPANY_INVITE_FILTERS.SEARCH === filter) {
        this.partnershipInviteFilterObj.filters.search = value
      } else if (PARTNER_COMPANY_INVITE_FILTERS.STATUS === filter) {
        this.partnershipInviteFilterObj.filters.status = value
      }
    },
    updatePartnershipFilters(value: any, filter?: PARTNER_COMPANY_FILTERS) {
      // TODO: Others over to this order as well.
      // Ordering should be updated on purpose not default to when filter does not match.
      if (filter === undefined) {
        this.partnershipFilterObj.ordering = value
      } else if (PARTNER_COMPANY_FILTERS.SEARCH === filter) {
        this.partnershipFilterObj.filters.search = value
      } else if (PARTNER_COMPANY_FILTERS.STATUS === filter) {
        this.partnershipFilterObj.filters.status = value
      }
    },
    updateTicketFilters(value: any, filter?: TICKET_FILTERS) {
      if (filter === undefined) {
        this.ticketFilterObj.ordering = value
      } else if (TICKET_FILTERS.SEARCH === filter) {
        this.ticketFilterObj.filters.search = value
      } else if (TICKET_FILTERS.SERVICE_HASH_IDS === filter) {
        this.ticketFilterObj.filters.service_hash_ids = value
      } else if (TICKET_FILTERS.IS_CANCELLED === filter) {
        this.ticketFilterObj.filters.is_cancelled = value
      } else if (TICKET_FILTERS.ACTIVATED_AT_RANGE === filter) {
        this.ticketFilterObj.filters.activated_at_range = value
      } else if (TICKET_FILTERS.VALID_RANGE === filter) {
        this.ticketFilterObj.filters.valid_range = value
      } else if (TICKET_FILTERS.VALIDATED_AT_RANGE === filter) {
        this.ticketFilterObj.filters.validated_at_range = value
      }
    },
    updateProductFilters(value: any, filter?: COMPUCASH_IMPORT_FILTERS) {
      if (filter === undefined) {
        this.productFilter.ordering = value
      } else if (COMPUCASH_IMPORT_FILTERS.SEARCH === filter) {
        this.productFilter.filters.search = value
      } else if (COMPUCASH_IMPORT_FILTERS.TOGGLE === filter) {
        this.productFilter.filters.without_service = value
      }
    },
    updateRouteServiceFilters(value: string, filter?: ROUTE_PLANNER_FILTERS) {
      if (ROUTE_PLANNER_FILTERS.SEARCH === filter) {
        this.routeServiceFilter.filters.search = value
      } else if (ROUTE_PLANNER_FILTERS.SEARCH_CATEGORIES === filter) {
        this.routeServiceFilter.filters.searchCategories = value
      } else if (ROUTE_PLANNER_FILTERS.MAP === filter) {
        this.routeServiceFilter.filters.dmoMap = value
      }
    },
    clearClientFilters() {
      this.clientFilter.filters.search = ''
      this.clientFilter.filters.isBusinessClient = false
    },
    clearServiceFilters() {
      this.serviceFilter.filters.categories = []
      this.serviceFilter.filters.type = null
      this.serviceFilter.filters.state =
        getServiceStateOptionList().find(
          (state) => state.key === SERVICE_STATE_FILTER.SHOW_ACTIVE,
        ) || getServiceStateOptionList()[1]
      this.serviceFilter.filters.private_state = getDefaultPrivateStateOption()
      this.clearOrdering(this.serviceFilter)
      this.serviceFilter.filters.labels = []
      this.serviceFilter.filters.search = ''
    },
    clearServiceEventFilters() {
      this.serviceEventFilter.filters = serviceEventDefaultFilterValues()
    },
    clearBookingFilters() {
      this.bookingFilter.filters.search = ''
      this.bookingFilter.filters.type = null
      this.bookingFilter.filters.date = null
      this.bookingFilter.filters.reservationStatuses = []
      this.bookingFilter.filters.payoutStatuses = []
      this.clearOrdering(this.bookingFilter)
    },
    clearResourceFilters() {
      this.resourceFilter.filters.type = null
      this.resourceFilter.filters.relatedUser = []
      this.clearOrdering(this.resourceFilter)
    },
    clearTaskFilters() {
      this.taskFilter.filters.search = ''
      this.taskFilter.filters.recipient = []
      this.taskFilter.filters.period = null
      this.taskFilter.filters.service = []
      this.taskFilter.filters.status = null
      this.clearOrdering(this.taskFilter)
    },
    clearPartnershipInviteFilters() {
      this.partnershipInviteFilter = getPartnershipInviteFilterDefaultValues()
    },
    clearPartnershipFilters() {
      this.partnershipFilter = getPartnershipFilterDefaultValues()
    },
    clearTicketFilters() {
      Object.assign(this.ticketFilter, getTicketFilterDefaultValues())
    },
    clearProductFilters() {
      this.productFilter.filters.search = ''
      this.productFilter.filters.without_service = false
      this.clearOrdering(this.productFilter)
    },
    clearOrdering(
      filterObj:
        | ServiceFilterState
        | BookingFilterState
        | ResourceFilterState
        | TaskFilterState
        | PartnershipInviteFilterState
        | PartnershipFilterState
        | TicketFilterState
        | ProductFilterState,
    ) {
      filterObj.ordering = {
        key: null,
        value: null,
        asc: null,
        desc: null,
      }
    },
  },
})
